import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { FiArrowLeft, FiFilter, FiInbox } from "react-icons/fi";
import SelectedMatchesCard from "./selectedMatchesCard";
import bettingService from "../../../api/services/bettingService";
import { playSong } from "../../common/actions/common.actions";
import { setSelectedMatchesDetail } from "../../common/components/sidebar/sidebar.actions";

const FILTERS = [
  { value: "all", label: "all" },
  { value: "mix", label: "maungg" },
  { value: "body", label: "body" },
];

const SelectedMatches = ({ setIsLoading }) => {
  const [showOptions, setShowOptions] = useState(false);
  const [selectedMatches, setSelectedMatches] = useState([]);
  const [filter, setFilter] = useState("all");
  const [hasError, setHasError] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSongClick = () => dispatch(playSong());

  const handleBackClick = () => {
    handleSongClick();
    navigate("/dashboard");
  };

  const handleOptionClick = (option) => {
    handleSongClick();
    setFilter(option);
    setShowOptions(false);
  };

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);

    bettingService
      .bettingMatchesHistory()
      .then((response) => response.json())
      .then((data) => {
        const matches = Array.isArray(data) ? data : [];
        const filteredMatches = matches.filter((match) => {
          if (filter === "body") return match?.gamblingTypeId === 1;
          if (filter === "mix") return match?.gamblingTypeId === 2;
          return true;
        });
        setSelectedMatches(filteredMatches);
      })
      .catch((error) => {
        setSelectedMatches([]);
        setHasError(true);
        console.error("API request error:", error);
      })
      .finally(() => setIsLoading(false));
  }, [filter, setIsLoading]);

  const sortedMatches = selectedMatches.slice().sort((a, b) =>
    new Date(b?.createdDate) - new Date(a?.createdDate)
  );
  const activeFilter = FILTERS.find((item) => item.value === filter);

  return (
    <div className="relative min-h-screen w-full min-w-0 flex-1 self-stretch overflow-hidden bg-gradient_start font-myanmarSansPro text-white">
      <div className="pointer-events-none absolute -right-24 top-20 h-72 w-72 rounded-full bg-emerald-300/10 blur-3xl" />

      <header className="relative z-30 bg-header_green shadow-lg shadow-black/20">
        <div className="mx-auto flex min-h-[60px] w-full max-w-2xl items-center px-4 py-2">
          <button
            type="button"
            onClick={handleBackClick}
            aria-label="Back to dashboard"
            className="flex h-10 w-10 flex-none items-center justify-center rounded-lg border border-white/10 bg-white/10 transition hover:bg-white/20 active:scale-95"
          >
            <FiArrowLeft className="h-5 w-5" />
          </button>

          <div className="ml-3 min-w-0">
            <h1 className="truncate text-base font-semibold leading-[1.8]">
              {t("selectedMatches")}
            </h1>
            <p className="font-sans text-[10px] uppercase tracking-[0.14em] text-white/40">
              {sortedMatches.length} {t("matches")}
            </p>
          </div>

          <div className="relative ml-auto">
            <button
              type="button"
              onClick={() => {
                handleSongClick();
                setShowOptions((visible) => !visible);
              }}
              aria-expanded={showOptions}
              aria-label="Filter selected matches"
              className={`flex h-10 items-center gap-2 rounded-lg border px-3 text-xs transition active:scale-95 ${
                showOptions
                  ? "border-emerald-200/30 bg-white text-header_green"
                  : "border-white/10 bg-white/10 text-white hover:bg-white/20"
              }`}
            >
              <FiFilter className="h-4 w-4" />
              <span>{t(activeFilter?.label || "all")}</span>
            </button>

            {showOptions && (
              <div className="absolute right-0 top-12 z-50 w-40 overflow-hidden rounded-lg border border-white/10 bg-header_green p-1.5 shadow-2xl shadow-black/40">
                {FILTERS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleOptionClick(option.value)}
                    className={`w-full rounded-md px-3 py-2 text-left text-xs leading-[1.8] transition ${
                      filter === option.value
                        ? "bg-white text-header_green"
                        : "text-white/65 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    {t(option.label)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto h-[calc(100vh-60px)] w-full max-w-2xl overflow-y-auto overscroll-contain px-4 pb-4 pt-3">
        {sortedMatches.length > 0 ? (
          <div className="space-y-2.5">
            {sortedMatches.map((data, index) => (
              <Link
                key={data?.gamblingId || data?.id || index}
                className="block"
                to={`/selected-matches-detail/${data?.gamblingId}`}
                onClick={() => {
                  dispatch(setSelectedMatchesDetail(data));
                  handleSongClick();
                }}
              >
                <SelectedMatchesCard match={data} />
              </Link>
            ))}
          </div>
        ) : (
          <div className="flex h-full min-h-[280px] flex-col items-center justify-center px-6 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-white/10 bg-header_green/50 text-white/45">
              <FiInbox className="h-6 w-6" />
            </div>
            <p className="mt-4 max-w-xs text-sm leading-[1.8] text-white/65">
              {hasError ? t("sendingFail") : t("noData")}
            </p>
            <p className="mt-1 text-xs text-white/35">{t(activeFilter?.label || "all")}</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default SelectedMatches;
